import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, Typography, Chip } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../AuthContext';
import { rootUrl } from '../../../../config/config';
import { formatMoney } from '../../owners/formatMoney';

export default function CarJobCardsList({ plate_no }) {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [jobCards, setJobCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchJobCards = async () => {
            if (!plate_no) return;
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`${rootUrl}/api/job-cards/car/${encodeURIComponent(plate_no)}`, {
                    headers: { Authorization: `Bearer ${authToken}` },
                    params: { limit: 100 }
                });

                const data = response.data?.data ?? response.data ?? [];
                setJobCards(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error fetching job cards:', err);
                setError(err.response?.data?.message || 'Failed to load job cards');
                setJobCards([]);
            } finally {
                setLoading(false);
            }
        };

        fetchJobCards();
    }, [plate_no, authToken]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getStatusColor = (status) => {
        const map = { OPEN: 'blue', IN_PROGRESS: 'amber', COMPLETED: 'green', COMPLETED_INVOICED: 'purple', AWAITING_INVOICE: 'indigo', CLOSED: 'gray', CANCELLED: 'red' };
        return map[status] || 'gray';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-[#FF5733] mr-3" />
                <Typography className="text-gray-600">Loading job cards...</Typography>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-6">
                <div className="bg-red-50 border border-red-200 rounded-sm p-4 text-red-700 text-sm">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex flex-col md:flex-row items-center justify-between mb-4 gap-4">
                <h3 className="text-base font-bold text-[#2a2e30]">Job Cards</h3>
                <Link
                    to="/job-cards/list"
                    className="px-3 py-2 bg-[#FF5733] text-white text-sm font-semibold rounded-sm hover:bg-[#e64a2b] transition-all duration-200 flex items-center gap-2"
                >
                    <FontAwesomeIcon icon={faClipboardList} />
                    View All Job Cards
                </Link>
            </div>

            {jobCards.length === 0 ? (
                <Card className="p-6 text-center rounded-xl shadow-none border border-gray-100">
                    <FontAwesomeIcon icon={faClipboardList} className="text-4xl text-gray-300 mx-auto mb-4" />
                    <Typography variant="h6" className="text-gray-600 mb-2 text-base">No job cards found for this car</Typography>
                    <Typography variant="small" className="text-gray-500">Job cards will appear here when created for {plate_no}</Typography>
                </Card>
            ) : (
                <div className="space-y-4">
                    <div className="md:hidden space-y-3">
                        {jobCards.map((card) => (
                            <Card key={card.id} className="p-4 rounded-xl shadow-none border border-gray-100">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <Link
                                            to={`/job-cards/${card.id}`}
                                            className="font-semibold text-[#FF5733] hover:text-[#e64a2b] hover:underline"
                                        >
                                            {card.job_card_no || card.id}
                                        </Link>
                                        <div className="mt-1 text-xs text-gray-500">{formatDate(card.opened_date)}</div>
                                    </div>
                                    <Chip
                                        value={card.status || 'N/A'}
                                        color={getStatusColor(card.status)}
                                        size="sm"
                                        className="w-fit text-[10px] py-0"
                                    />
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <div className="text-xs text-gray-500">Total Cost</div>
                                        <div className="font-semibold text-gray-800">{formatMoney(card.total_cost)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-500">Invoiced</div>
                                        {card.invoiced ? (
                                            <Chip value="Yes" color="green" size="sm" className="w-fit mt-1 text-[10px] py-0" />
                                        ) : (
                                            <Chip value="No" color="gray" size="sm" className="w-fit mt-1 text-[10px] py-0" />
                                        )}
                                    </div>
                                    <div className="col-span-2">
                                        <div className="text-xs text-gray-500">Invoice Date</div>
                                        <div className="font-medium text-gray-700">{formatDate(card.invoice_date)}</div>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>

                    <div className="hidden md:block overflow-x-auto">
                        <div className="border border-gray-300 rounded-sm overflow-hidden">
                        <table className="w-full min-w-[640px]">
                            <thead>
                                <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                    <th className="text-left py-2 px-2 text-xs font-semibold text-gray-800 leading-none">Job Card No</th>
                                    <th className="text-left py-2 px-2 text-xs font-semibold text-gray-800 leading-none">Status</th>
                                    <th className="text-left py-2 px-2 text-xs font-semibold text-gray-800 leading-none">Opened</th>
                                    <th className="text-right py-2 px-2 text-xs font-semibold text-gray-800 leading-none">Total Cost</th>
                                    <th className="text-left py-2 px-2 text-xs font-semibold text-gray-800 leading-none">Invoiced</th>
                                    <th className="text-left py-2 px-2 text-xs font-semibold text-gray-800 leading-none">Invoice Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {jobCards.map((card, idx) => (
                                    <tr
                                        key={card.id}
                                        className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors duration-150`}
                                    >
                                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                                            <Link
                                                to={`/job-cards/${card.id}`}
                                                className="font-semibold text-[#FF5733] hover:text-[#e64a2b] hover:underline"
                                            >
                                                {card.job_card_no || card.id}
                                            </Link>
                                        </td>
                                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                                            <Chip
                                                value={card.status || 'N/A'}
                                                color={getStatusColor(card.status)}
                                                size="sm"
                                                className="w-fit text-[10px] py-0"
                                            />
                                        </td>
                                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700">{formatDate(card.opened_date)}</td>
                                        <td className="px-2 py-2 whitespace-nowrap text-xs text-right font-medium">{formatMoney(card.total_cost)}</td>
                                        <td className="px-2 py-2 whitespace-nowrap text-xs">
                                            {card.invoiced ? (
                                                <Chip value="Yes" color="green" size="sm" className="w-fit text-[10px] py-0" />
                                            ) : (
                                                <Chip value="No" color="gray" size="sm" className="w-fit text-[10px] py-0" />
                                            )}
                                        </td>
                                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700">{formatDate(card.invoice_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
